import { useState, useEffect, useRef } from 'react';
import { adminStore, DEFAULT_VIDEOS, type VideoItem } from '../../../store/adminStore';

function extractYoutubeId(input: string): string {
  const trimmed = input.trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;
  const shortMatch = trimmed.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  if (shortMatch) return shortMatch[1];
  const watchMatch = trimmed.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
  if (watchMatch) return watchMatch[1];
  const embedMatch = trimmed.match(/embed\/([a-zA-Z0-9_-]{11})/);
  if (embedMatch) return embedMatch[1];
  return trimmed;
}

function isValidYoutubeId(id: string): boolean {
  return /^[a-zA-Z0-9_-]{11}$/.test(id);
}

// Store local video blob URLs in memory (not localStorage — too large)
const localVideoBlobMap: Record<string, string> = {};

export function getLocalVideoUrl(id: string): string {
  return localVideoBlobMap[id] ?? '';
}

function setLocalVideoUrl(id: string, url: string) {
  localVideoBlobMap[id] = url;
}

// ─── AddVideoModal ─────────────────────────────────────────────────────────────
type AddTab = 'upload' | 'youtube';

interface AddVideoModalProps {
  onClose: () => void;
  onAdd: (video: Omit<VideoItem, 'id'>, file?: File) => void;
}

function AddVideoModal({ onClose, onAdd }: AddVideoModalProps) {
  const [tab, setTab] = useState<AddTab>('upload');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // Upload tab
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // YouTube tab
  const [linkInput, setLinkInput] = useState('');
  const parsedId = extractYoutubeId(linkInput);
  const isValidYT = isValidYoutubeId(parsedId);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      setError('Vui lòng chọn file video (mp4, mov, avi...)');
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      setError('File quá lớn (tối đa 500MB)');
      return;
    }
    setError('');
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    if (!title) setTitle(file.name.replace(/\.[^.]+$/, ''));
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const fakeEvent = { target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>;
    handleFileChange(fakeEvent);
  };

  const handleSubmit = () => {
    if (!title.trim()) { setError('Vui lòng nhập tiêu đề'); return; }

    if (tab === 'upload') {
      if (!selectedFile) { setError('Vui lòng chọn file video'); return; }
      setError('');
      onAdd({ title: title.trim(), type: 'local', description: description.trim() }, selectedFile);
      setSaved(true);
    } else {
      if (!linkInput.trim()) { setError('Vui lòng nhập link YouTube'); return; }
      if (!isValidYT) { setError('Link YouTube không hợp lệ'); return; }
      setError('');
      onAdd({ title: title.trim(), youtubeId: parsedId, type: 'youtube', description: description.trim() });
      setSaved(true);
    }
  };

  const handleAddAnother = () => {
    setSaved(false);
    setTitle('');
    setDescription('');
    setLinkInput('');
    setSelectedFile(null);
    setPreviewUrl('');
    setError('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-3xl max-w-lg w-full overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div
          className="px-7 py-5 flex items-center justify-between flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #d4521a, #f5b942)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-xl">
              <i className="ri-video-add-fill text-white text-lg"></i>
            </div>
            <div>
              <h3 className="text-base font-extrabold text-white">Thêm video mới</h3>
              <p className="text-xs text-white/80">Tải lên hoặc dán link YouTube</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center bg-white/20 hover:bg-white/35 rounded-xl transition-all cursor-pointer"
          >
            <i className="ri-close-line text-white text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-7 py-6">
          {saved ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-check-fill text-3xl text-green-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-1">Đã thêm video!</h3>
              <p className="text-gray-400 text-sm mb-6">Video đã xuất hiện trên trang Hạt giống tri thức.</p>
              <div className="flex gap-3">
                <button
                  onClick={handleAddAnother}
                  className="flex-1 py-3 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-pointer hover:bg-gray-50 whitespace-nowrap"
                >
                  Thêm video khác
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-yellow-400 text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
                >
                  Xong
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Tab switcher */}
              <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
                <button
                  onClick={() => { setTab('upload'); setError(''); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    tab === 'upload' ? 'bg-white text-orange-500 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className="ri-upload-cloud-2-line"></i>
                  Tải video lên
                </button>
                <button
                  onClick={() => { setTab('youtube'); setError(''); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    tab === 'youtube' ? 'bg-white text-red-500 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className="ri-youtube-fill"></i>
                  Link YouTube
                </button>
              </div>

              {/* Upload tab */}
              {tab === 'upload' && (
                <div>
                  {!selectedFile ? (
                    <div
                      onDrop={handleDrop}
                      onDragOver={e => e.preventDefault()}
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-orange-200 rounded-2xl p-8 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/50 transition-all"
                    >
                      <div className="w-14 h-14 flex items-center justify-center bg-orange-100 rounded-2xl mx-auto mb-3">
                        <i className="ri-upload-cloud-2-fill text-orange-400 text-2xl"></i>
                      </div>
                      <p className="font-semibold text-gray-700 mb-1">Kéo thả video vào đây</p>
                      <p className="text-sm text-gray-400 mb-3">hoặc nhấn để chọn file</p>
                      <p className="text-xs text-gray-300">MP4, MOV, AVI, WebM — tối đa 500MB</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </div>
                  ) : (
                    <div className="rounded-2xl overflow-hidden border border-gray-100 bg-gray-50">
                      <video
                        src={previewUrl}
                        controls
                        className="w-full max-h-48 object-contain bg-black"
                      />
                      <div className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                            <i className="ri-file-video-fill text-orange-400 text-sm"></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-700 truncate">{selectedFile.name}</p>
                            <p className="text-xs text-gray-400">{(selectedFile.size / 1024 / 1024).toFixed(1)} MB</p>
                          </div>
                        </div>
                        <button
                          onClick={() => { setSelectedFile(null); setPreviewUrl(''); }}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-100 transition-colors cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* YouTube tab */}
              {tab === 'youtube' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Link YouTube <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={linkInput}
                    onChange={e => { setLinkInput(e.target.value); setError(''); }}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors"
                  />
                  {linkInput && isValidYT && (
                    <p className="text-xs text-green-600 mt-1">
                      ID xác nhận: <span className="font-mono font-semibold">{parsedId}</span>
                    </p>
                  )}
                  {linkInput && !isValidYT && (
                    <p className="text-xs text-red-400 mt-1">Link không hợp lệ, vui lòng kiểm tra lại</p>
                  )}
                  {linkInput && isValidYT && (
                    <div className="mt-3 rounded-xl overflow-hidden border border-gray-100">
                      <img
                        src={`https://img.youtube.com/vi/${parsedId}/hqdefault.jpg`}
                        alt="Thumbnail xem trước"
                        className="w-full h-36 object-cover"
                      />
                      <p className="text-xs text-gray-400 text-center py-2 bg-gray-50">Xem trước thumbnail</p>
                    </div>
                  )}
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Tiêu đề video <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={e => { setTitle(e.target.value); setError(''); }}
                  placeholder="VD: Lễ hội cồng chiêng Tây Nguyên"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Mô tả ngắn <span className="text-gray-400 font-normal">(tuỳ chọn)</span>
                </label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={2}
                  placeholder="Mô tả ngắn về nội dung video..."
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors resize-none"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl">
                  <i className="ri-error-warning-line text-red-400"></i>
                  <p className="text-sm text-red-500">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button
                  onClick={onClose}
                  className="flex-1 py-3 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-pointer hover:bg-gray-50 whitespace-nowrap"
                >
                  Hủy
                </button>
                <button
                  onClick={handleSubmit}
                  className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-yellow-400 text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line mr-1.5"></i>Thêm video
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── EditVideoModal ────────────────────────────────────────────────────────────
interface EditVideoModalProps {
  video: VideoItem;
  onClose: () => void;
  onSave: (video: VideoItem) => void;
}

function EditVideoModal({ video, onClose, onSave }: EditVideoModalProps) {
  const [title, setTitle] = useState(video.title);
  const [linkInput, setLinkInput] = useState(video.youtubeId ?? '');
  const [description, setDescription] = useState(video.description ?? '');
  const [error, setError] = useState('');

  const parsedId = extractYoutubeId(linkInput);
  const isValid = isValidYoutubeId(parsedId);

  const handleSave = () => {
    if (!title.trim()) { setError('Vui lòng nhập tiêu đề'); return; }
    if (video.type === 'youtube' && !isValid) { setError('Link YouTube không hợp lệ'); return; }
    onSave({ ...video, title: title.trim(), youtubeId: video.type === 'youtube' ? parsedId : undefined, description: description.trim() });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-3xl max-w-lg w-full overflow-hidden">
        <div
          className="px-7 py-5 flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg, #d4521a, #f5b942)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-xl">
              <i className="ri-edit-line text-white text-lg"></i>
            </div>
            <h3 className="text-base font-extrabold text-white">Chỉnh sửa video</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center bg-white/20 hover:bg-white/35 rounded-xl transition-all cursor-pointer">
            <i className="ri-close-line text-white text-xl"></i>
          </button>
        </div>

        <div className="px-7 py-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tiêu đề <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={title}
              onChange={e => { setTitle(e.target.value); setError(''); }}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors"
            />
          </div>

          {video.type === 'youtube' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Link YouTube <span className="text-red-400">*</span></label>
              <input
                type="text"
                value={linkInput}
                onChange={e => { setLinkInput(e.target.value); setError(''); }}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors"
              />
              {linkInput && isValid && (
                <p className="text-xs text-green-600 mt-1">ID: <span className="font-mono font-semibold">{parsedId}</span></p>
              )}
            </div>
          )}

          {video.type === 'local' && (
            <div className="p-3 bg-orange-50 rounded-xl flex items-center gap-2">
              <i className="ri-file-video-fill text-orange-400"></i>
              <p className="text-sm text-orange-700">Video đã tải lên — không thể thay đổi file, chỉ chỉnh sửa tiêu đề và mô tả.</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Mô tả ngắn</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors resize-none"
            />
          </div>

          {video.type === 'youtube' && linkInput && isValid && (
            <div className="rounded-xl overflow-hidden border border-gray-100">
              <img src={`https://img.youtube.com/vi/${parsedId}/hqdefault.jpg`} alt="thumbnail" className="w-full h-32 object-cover" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl">
              <i className="ri-error-warning-line text-red-400"></i>
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-3 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-pointer hover:bg-gray-50 whitespace-nowrap">Hủy</button>
            <button onClick={handleSave} className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-yellow-400 text-white font-bold rounded-xl hover:opacity-90 cursor-pointer whitespace-nowrap">Lưu thay đổi</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── KnowledgeEditor ──────────────────────────────────────────────────────────
export default function KnowledgeEditor() {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editVideo, setEditVideo] = useState<VideoItem | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const refresh = () => setVideos(adminStore.getStore().videos);

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener('bahnar-admin-updated', handler);
    return () => window.removeEventListener('bahnar-admin-updated', handler);
  }, []);

  const handleAdd = (videoData: Omit<VideoItem, 'id'>, file?: File) => {
    const newId = `video-${Date.now()}`;
    const newVideo: VideoItem = { ...videoData, id: newId };
    if (file) {
      const blobUrl = URL.createObjectURL(file);
      setLocalVideoUrl(newId, blobUrl);
      newVideo.localUrl = blobUrl;
    }
    adminStore.updateVideos([...videos, newVideo]);
    refresh();
  };

  const handleEdit = (updated: VideoItem) => {
    adminStore.updateVideos(videos.map(v => v.id === updated.id ? updated : v));
    refresh();
  };

  const handleDelete = (id: string) => {
    adminStore.updateVideos(videos.filter(v => v.id !== id));
    setDeleteId(null);
    refresh();
  };

  const handleReset = () => {
    adminStore.updateVideos([...DEFAULT_VIDEOS]);
    refresh();
  };

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-bold text-gray-800">Video – Hạt giống tri thức</h2>
          <p className="text-xs text-gray-400">{videos.length} video đang hiển thị trên trang</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Khôi phục mặc định
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-yellow-400 text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Thêm video
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div className="mb-4 p-4 bg-orange-50 rounded-2xl flex items-start gap-3">
        <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
          <i className="ri-information-line text-orange-500"></i>
        </div>
        <div>
          <p className="text-sm text-orange-700 leading-relaxed">
            Bạn có thể <strong>tải video trực tiếp từ máy tính</strong> hoặc dán <strong>link YouTube</strong>. Video sẽ xuất hiện ngay trên trang <strong>Hạt giống tri thức</strong>.
          </p>
          <p className="text-xs text-orange-500 mt-1">
            Lưu ý: Video tải lên chỉ hiển thị trong phiên làm việc hiện tại. Để lưu vĩnh viễn, hãy dùng link YouTube.
          </p>
        </div>
      </div>

      {/* Video list */}
      <div className="space-y-3">
        {videos.map((video, idx) => (
          <div key={video.id} className="bg-white rounded-2xl p-4 flex items-center gap-4">
            {/* Thumbnail */}
            <div className="w-28 h-16 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100 relative">
              {video.type === 'youtube' && video.youtubeId ? (
                <img
                  src={`https://img.youtube.com/vi/${video.youtubeId}/mqdefault.jpg`}
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-100 to-yellow-100">
                  <i className="ri-file-video-fill text-orange-400 text-2xl"></i>
                </div>
              )}
              {/* Type badge */}
              <div className={`absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                video.type === 'youtube' ? 'bg-red-500 text-white' : 'bg-orange-500 text-white'
              }`}>
                {video.type === 'youtube' ? 'YT' : 'UP'}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-bold text-orange-400">#{idx + 1}</span>
                <h3 className="font-bold text-sm text-gray-800 truncate">{video.title}</h3>
              </div>
              {video.type === 'youtube' && video.youtubeId && (
                <p className="text-xs text-gray-400 font-mono truncate">ID: {video.youtubeId}</p>
              )}
              {video.type === 'local' && (
                <p className="text-xs text-orange-500 font-medium">Video đã tải lên</p>
              )}
              {video.description && (
                <p className="text-xs text-gray-400 mt-0.5 truncate">{video.description}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {video.type === 'youtube' && video.youtubeId && (
                <a
                  href={`https://www.youtube.com/watch?v=${video.youtubeId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-50 text-red-400 hover:bg-red-100 transition-colors cursor-pointer"
                  title="Xem trên YouTube"
                >
                  <i className="ri-youtube-fill text-sm"></i>
                </a>
              )}
              <button
                onClick={() => setEditVideo(video)}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors cursor-pointer"
                title="Chỉnh sửa"
              >
                <i className="ri-edit-line text-sm"></i>
              </button>
              <button
                onClick={() => setDeleteId(video.id)}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-50 text-red-400 hover:bg-red-100 transition-colors cursor-pointer"
                title="Xóa"
              >
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
            </div>
          </div>
        ))}

        {videos.length === 0 && (
          <div className="bg-white rounded-2xl p-10 text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-gray-50 rounded-2xl mx-auto mb-3">
              <i className="ri-youtube-line text-2xl text-gray-300"></i>
            </div>
            <p className="text-sm text-gray-400">Chưa có video nào. Nhấn <strong>Thêm video</strong> để bắt đầu!</p>
          </div>
        )}
      </div>

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl max-w-sm w-full p-8 text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-2-fill text-red-500 text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Xóa video?</h3>
            <p className="text-gray-400 text-sm mb-6">Video sẽ bị xóa khỏi trang web ngay lập tức.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-3 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-pointer whitespace-nowrap hover:bg-gray-50">Hủy</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 cursor-pointer whitespace-nowrap">Xóa ngay</button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && <AddVideoModal onClose={() => setShowAddModal(false)} onAdd={handleAdd} />}
      {editVideo && <EditVideoModal video={editVideo} onClose={() => setEditVideo(null)} onSave={handleEdit} />}
    </div>
  );
}
